import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { RefreshCw, Heart, Shirt, Clock, Plus, ArrowUp, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useSavedItems } from '@/hooks/useSavedItems';
import { useProfile } from '@/hooks/useProfile';
import { useUserPersona } from '@/hooks/useUserPersona';
import { useRegionalSettings } from '@/hooks/useRegionalSettings';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { OptimizedImage } from '@/components/ui/OptimizedImage';
import { SaveButton } from '@/components/ui/SaveButton';
import { getCachedQueryData, setCachedQueryData, getCacheKey } from '@/lib/queryPersistence';
import { Button } from '@/components/ui/button';
import { ForYouSkeleton } from '@/components/skeletons/ForYouSkeleton';

import { ProductPreviewLightbox, PreviewProduct } from '@/components/ui/ProductPreviewLightbox';
import { SuggestionFriendPicker } from '@/components/share/SuggestionFriendPicker';
const MIN_ITEMS_REQUIRED = 5;
interface RecommendedProduct {
  id: string;
  name: string;
  price: number | null;
  currency: string;
  store: string;
  image: string;
  url: string;
  reason?: string;
  category?: string;
}
interface RecommendationSection {
  title: string;
  subtitle?: string;
  products: RecommendedProduct[];
  emoji?: string;
}
function ProductCard({
  product,
  onSave,
  formatPrice,
  delay = 0,
  isSaved = false,
  onPreview,
}: {
  product: RecommendedProduct;
  onSave: () => void;
  formatPrice: (price: number | null, currency?: string) => string | null;
  delay?: number;
  isSaved?: boolean;
  onPreview: () => void;
}) {
  const navigate = useNavigate();
  const handleClick = () => {
    onPreview();
  };
  const handleTryOn = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate('/app/try-on', {
      state: {
        productImage: product.image,
        productName: product.name
      }
    });
  };
  const formattedPrice = formatPrice(product.price, product.currency);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    delay
  }} className="cursor-pointer" onClick={handleClick}>
      <div className="rounded-xl overflow-hidden bg-card border border-border/50">
        <div className="relative aspect-square bg-surface-2">
          <OptimizedImage src={product.image} alt={product.name} className="w-full h-full" style={{
          objectFit: 'cover'
        }} />

          {/* Price pill */}
          {formattedPrice && <div className="absolute bottom-2 left-2 bg-background/90 backdrop-blur-sm rounded-full px-2 py-0.5 shadow-sm leading-[115%]">
              <span className="text-xs font-semibold text-foreground">{formattedPrice}</span>
            </div>}

          {/* Action buttons */}
          <div className="absolute top-2 right-2 flex flex-col gap-1.5">
            <SaveButton saved={isSaved} onToggle={onSave} size="sm" variant="overlay" />
            <button onClick={handleTryOn} className="w-8 h-8 rounded-full bg-black/40 backdrop-blur-md text-white border border-white/20 shadow-md flex items-center justify-center active:bg-black/60 active:scale-95 transition-all">
              <Shirt className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="p-2.5">
          <p className="text-xs text-muted-foreground truncate">{product.store}</p>
          <p className="text-sm font-medium text-foreground line-clamp-1">{product.name}</p>
        </div>
      </div>
    </motion.div>;
}
function SectionHeader({
  title,
  subtitle,
  emoji
}: {
  title: string;
  subtitle?: string;
  emoji?: string;
}) {
  return <div className="mb-4">
      <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
        {emoji && <span>{emoji}</span>}
        {title}
      </h2>
      {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>;
}
interface ForYouTabContentProps {
  onRefreshRequest?: () => void;
}
export function ForYouTabContent({
  onRefreshRequest
}: ForYouTabContentProps) {
  const navigate = useNavigate();
  const {
    items,
    isLoading: isItemsLoading
  } = useSavedItems();
  const {
    profile,
    updateProfile
  } = useProfile();
  const {
    personaData,
    updateAiNotes
  } = useUserPersona();
  const {
    formatPrice,
    settings
  } = useRegionalSettings();
  const {
    createItem
  } = useSavedItems();
  const [sections, setSections] = useState<RecommendationSection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [retryAfter, setRetryAfter] = useState(0);
  const [savedProductUrls, setSavedProductUrls] = useState<Set<string>>(new Set());
  const [hintInput, setHintInput] = useState('');
  const [isSavingHint, setIsSavingHint] = useState(false);
  const [previewProduct, setPreviewProduct] = useState<RecommendedProduct | null>(null);
  const [showFriendPicker, setShowFriendPicker] = useState(false);

  // Read persisted refinement hint from profile
  const activeHint = useMemo(() => {
    const uiPrefs = (profile as any)?.ui_preferences as Record<string, any> | null;
    return uiPrefs?.recommendation_hint as string || '';
  }, [profile]);
  const abortRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Build a set of saved item URLs for quick lookup
  const existingSavedUrls = useMemo(() => {
    const urls = new Set<string>();
    items?.forEach(item => {
      if (item.url) urls.add(item.url);
    });
    return urls;
  }, [items]);

  // Build context for AI recommendations - use stable references
  const itemsLength = items?.length || 0;
  const profileGender = profile?.gender || null;
  const currency = settings?.currency || 'GBP';
  const country = settings?.countryCode || 'GB';

  // Cache key for localStorage persistence
  const cacheKey = useMemo(() => getCacheKey(['for-you-recommendations', profileGender || 'any', country]), [profileGender, country]);

  // Memoize expensive computations with stable primitive deps
  const userContext = useMemo(() => {
    const recentItems = items?.slice(0, 10) || [];
    const categories = recentItems.map(i => i.ai_category).filter(Boolean);
    const stores = recentItems.map(i => i.store).filter(Boolean);
    const priceRange = recentItems.filter(i => i.price).map(i => i.price!);
    return {
      savedItemsCount: itemsLength,
      recentCategories: [...new Set(categories)].slice(0, 5),
      favoriteStores: [...new Set(stores)].slice(0, 5),
      avgPrice: priceRange.length > 0 ? priceRange.reduce((a, b) => a + b, 0) / priceRange.length : null,
      gender: profileGender,
      styleKeywords: personaData?.preferences?.style_keywords || [],
      favoriteBrands: personaData?.preferences?.favorite_brands || [],
      currency,
      country
    };
  }, [itemsLength, profileGender, currency, country, items, personaData?.preferences]);

  // Fetch recommendations with caching
  const fetchRecommendations = useCallback(async (forceRefresh = false) => {
    // Don't fetch if items haven't loaded yet
    if (isItemsLoading) return;

    // Not enough items - dismiss loading
    if (itemsLength < MIN_ITEMS_REQUIRED) {
      setIsLoading(false);
      return;
    }

    // Check cache first (unless forcing refresh)
    if (!forceRefresh) {
      const cached = getCachedQueryData<RecommendationSection[]>(cacheKey, 30 * 60 * 1000); // 30 min cache
      if (cached && cached.length > 0) {
        setSections(cached);
        setIsLoading(false);
        return;
      }
    }
    setIsLoading(true);
    setIsRateLimited(false);
    

    // Abort any in-flight request to prevent result "flashing" (e.g. StrictMode double-invoke)
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/personalized-recommendations`, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          userContext,
          refinementHint: activeHint || undefined
        })
      });
      if (response.status === 429) {
        setIsRateLimited(true);
        setRetryAfter(60); // Default 60 second retry
        // Try to use stale cache if available
        const staleCache = getCachedQueryData<RecommendationSection[]>(cacheKey, 24 * 60 * 60 * 1000);
        if (staleCache && staleCache.length > 0) {
          setSections(staleCache);
          toast('Showing your recent picks', {
            description: 'New picks available soon'
          });
        }
        return;
      }
      if (!response.ok) {
        throw new Error('Failed to get recommendations');
      }
      const data = await response.json();
      const newSections = data.sections || [];
      setSections(newSections);

      // Cache the results
      if (newSections.length > 0) {
        setCachedQueryData(cacheKey, newSections);
      }
    } catch (error: any) {
      // Ignore intentional aborts
      if (error?.name === 'AbortError') return;
      console.error('Error fetching recommendations:', error);
      // Try stale cache on error
      const staleCache = getCachedQueryData<RecommendationSection[]>(cacheKey, 24 * 60 * 60 * 1000);
      if (staleCache && staleCache.length > 0) {
        setSections(staleCache);
      } else {
        toast.error('Could not load recommendations');
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setIsLoading(false);
    }
  }, [cacheKey, userContext, itemsLength, activeHint, isItemsLoading]);

  // Countdown timer for rate limit
  useEffect(() => {
    if (retryAfter <= 0) return;
    const timer = setInterval(() => {
      setRetryAfter(prev => {
        if (prev <= 1) {
          setIsRateLimited(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [retryAfter]);

  // Initial fetch on mount
  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);



  const handleSaveProduct = async (product: RecommendedProduct) => {
    // Optimistically add to saved URLs immediately
    if (product.url) {
      setSavedProductUrls(prev => new Set(prev).add(product.url));
    }
    try {
      await createItem.mutateAsync({
        name: product.name,
        url: product.url,
        image_url: product.image,
        price: product.price || undefined,
        currency: product.currency,
        store: product.store,
        source_type: 'url',
        save_source: 'for_you',
      });
    } catch (error) {
      console.error('Error saving product:', error);
      // Rollback on error
      if (product.url) {
        setSavedProductUrls(prev => {
          const next = new Set(prev);
          next.delete(product.url);
          return next;
        });
      }
      toast.error('Could not save item');
    }
  };
  const handleRefresh = useCallback(() => {
    fetchRecommendations(true);
    onRefreshRequest?.();
  }, [fetchRecommendations, onRefreshRequest]);
  const handleSubmitHint = useCallback(async () => {
    const hint = hintInput.trim();
    if (!hint || isSavingHint) return;
    setIsSavingHint(true);
    try {
      // Persist to profile ui_preferences (merge with existing)
      const existingPrefs = (profile as any)?.ui_preferences as Record<string, any> || {};
      const {
        error
      } = await supabase.from('profiles').update({
        ui_preferences: {
          ...existingPrefs,
          recommendation_hint: hint
        }
      }).eq('user_id', profile!.user_id);
      if (error) throw error;

      // Also update persona AI notes
      if (personaData) {
        const currentNotes = personaData.ai_notes || '';
        const notePrefix = '[Recommendation preference] ';
        // Remove any existing recommendation preference note
        const cleanedNotes = currentNotes.replace(/\[Recommendation preference\].*?(?:\n|$)/g, '').trim();
        const newNotes = cleanedNotes ? `${cleanedNotes}\n${notePrefix}${hint}` : `${notePrefix}${hint}`;
        updateAiNotes.mutate({
          userId: profile!.user_id,
          notes: newNotes
        });
      }
      setHintInput('');
      // Saved silently
      // Force refresh recommendations with new hint
      fetchRecommendations(true);
    } catch (err) {
      console.error('Error saving hint:', err);
      toast.error('Could not save preference');
    } finally {
      setIsSavingHint(false);
    }
  }, [hintInput, isSavingHint, profile, updateProfile, personaData, updateAiNotes, fetchRecommendations]);
  const handleClearHint = useCallback(async () => {
    setIsSavingHint(true);
    try {
      const existingPrefs = (profile as any)?.ui_preferences as Record<string, any> || {};
      const {
        recommendation_hint,
        ...rest
      } = existingPrefs;
      await supabase.from('profiles').update({
        ui_preferences: Object.keys(rest).length > 0 ? rest : null
      }).eq('user_id', profile!.user_id);
      // Cleared silently
      fetchRecommendations(true);
    } catch (err) {
      console.error('Error clearing hint:', err);
      toast.error('Could not clear preference');
    } finally {
      setIsSavingHint(false);
    }
  }, [profile, fetchRecommendations]);

  // Pull-to-refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const isPulling = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const scrollParent = containerRef.current?.closest('[style*="overflow"], .overflow-y-auto') as HTMLElement;
    const scrollTop = scrollParent?.scrollTop ?? 0;
    if (scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling.current || isRefreshing) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0) {
      setPullDistance(Math.min(diff * 0.4, 80));
    }
  }, [isRefreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling.current) return;
    isPulling.current = false;
    if (pullDistance > 50 && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(50);
      await fetchRecommendations(true);
      setIsRefreshing(false);
    }
    setPullDistance(0);
  }, [pullDistance, isRefreshing, fetchRecommendations]);

  if (isItemsLoading) {
    return <ForYouSkeleton />;
  }

  // Show loading while recommendations are being fetched
  const showInitialLoading = isLoading && sections.length === 0;
  if (showInitialLoading) {
    return <ForYouSkeleton />;
  }
  if (itemsLength < MIN_ITEMS_REQUIRED) {
    return <div className="flex flex-col items-center justify-center py-20 px-6">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
          <Plus className="w-10 h-10 text-primary" />
        </div>
        <h2 className="text-lg font-semibold text-foreground mb-2">Keep saving!</h2>
        <p className="text-sm text-muted-foreground text-center max-w-xs mb-4">
          Save {MIN_ITEMS_REQUIRED - itemsLength} more items to unlock personalized recommendations
        </p>
        <div className="w-48 h-2 bg-muted rounded-full overflow-hidden mb-2">
          <div className="h-full bg-primary rounded-full transition-all duration-300" style={{
          width: `${itemsLength / MIN_ITEMS_REQUIRED * 100}%`
        }} />
        </div>
        <p className="text-xs text-muted-foreground">
          {itemsLength} of {MIN_ITEMS_REQUIRED} items saved
        </p>
        <Button variant="default" size="sm" className="mt-6" onClick={() => navigate('/app')}>
          <Plus className="w-4 h-4 mr-2" />
          Add more items
        </Button>
      </div>;
  }
  if (sections.length === 0 && isRateLimited) {
    return <div className="flex flex-col items-center justify-center py-20 px-6">
        <Clock className="w-12 h-12 text-muted-foreground/30 mb-4" />
        <h2 className="text-lg font-semibold text-foreground mb-2">Taking a breather</h2>
        <p className="text-sm text-muted-foreground text-center max-w-xs mb-4">
          We're preparing fresh recommendations for you. Please wait a moment.
        </p>
        {retryAfter > 0 && <p className="text-sm text-primary font-medium">
            Ready in {retryAfter}s
          </p>}
        {retryAfter === 0 && <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Try again
          </Button>}
      </div>;
  }
  if (sections.length === 0) {
    return <ForYouSkeleton />;
  }


  return <div
      ref={containerRef}
      className="-mx-5 px-4 py-2 space-y-6 pb-nav"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull to refresh indicator */}
      <AnimatePresence>
        {(pullDistance > 0 || isRefreshing) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: pullDistance > 0 ? pullDistance : 50, opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex items-center justify-center overflow-hidden"
          >
            <RefreshCw className={cn("w-5 h-5 text-muted-foreground", isRefreshing && "animate-spin", pullDistance > 50 && "text-primary")} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Personalization signal - subtle inline */}
      {itemsLength > 0 && <p className="text-[11px] text-muted-foreground/60 text-center">
          Based on {itemsLength.toLocaleString()} saves · Pull to refresh
        </p>}

      {/* Recommendation Sections */}
      {sections.map((section, sectionIndex) => <motion.section key={section.title} initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      delay: sectionIndex * 0.1
    }}>
          {sectionIndex > 0 && <div className="h-px bg-border mb-5" />}
          <SectionHeader title={section.title} subtitle={section.subtitle} emoji={section.emoji} />
          <div className="grid grid-cols-2 gap-3">
            {section.products.map((product, productIndex) => <ProductCard key={product.id} product={product} onSave={() => handleSaveProduct(product)} formatPrice={formatPrice} delay={productIndex * 0.05} isSaved={existingSavedUrls.has(product.url) || savedProductUrls.has(product.url)} onPreview={() => setPreviewProduct(product)} />)}
          </div>
        </motion.section>)}

      {/* Refinement input - moved to bottom */}
      <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-2">
        <div className="h-px bg-border my-5" />
        <AnimatePresence mode="wait">
          {activeHint ? <motion.div key="active-hint" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Showing:</span>
              <span className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full px-3 py-1">
                {activeHint}
                <button onClick={handleClearHint} disabled={isSavingHint} className="hover:bg-primary/20 rounded-full p-0.5 transition-colors">
                  <X className="w-3 h-3" />
                </button>
              </span>
              <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={isLoading} className="ml-auto gap-1.5 h-7 text-xs">
                <RefreshCw className={cn("w-3.5 h-3.5", isLoading && "animate-spin")} />
                Refresh
              </Button>
            </motion.div> : <motion.div key="hint-input" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }}>
              <p className="text-sm font-medium text-foreground mb-2">How can we improve these picks?</p>
              <div className="bg-surface-2 focus-within:bg-surface-3 transition-colors rounded-[24px] flex items-end pl-4 pr-1.5 gap-1">
                <textarea
                  value={hintInput}
                  onChange={e => {
                    setHintInput(e.target.value.slice(0, 200));
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                  }}
                  placeholder="e.g. fewer sneakers, more summer dresses"
                  className="w-full min-h-[48px] max-h-[120px] bg-transparent py-3 text-foreground resize-none focus:outline-none leading-relaxed overflow-y-auto text-sm"
                  rows={2}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmitHint();
                    }
                  }}
                  disabled={isSavingHint}
                />
                <button
                  onClick={handleSubmitHint}
                  disabled={!hintInput.trim() || isSavingHint}
                  className={cn(
                    "w-8 h-8 mb-2 rounded-full flex items-center justify-center shrink-0 transition-colors disabled:opacity-50",
                    hintInput.trim() ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                  )}
                >
                  <ArrowUp className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                What you ask for here will apply across your whole WNTD experience                
              </p>

              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Results still not feeling right?{' '}
                  <Link to="/settings/profile-style" className="text-primary hover:underline font-medium">
                    Reset your style profile
                  </Link>{' '}
                  to start fresh.
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.div>

      {/* Product Preview Lightbox */}
      <ProductPreviewLightbox
        open={!!previewProduct}
        onClose={() => setPreviewProduct(null)}
        product={previewProduct ? {
          name: previewProduct.name,
          image: previewProduct.image,
          url: previewProduct.url,
          price: previewProduct.price,
          currency: previewProduct.currency,
          store: previewProduct.store,
        } : null}
        onSave={() => previewProduct && handleSaveProduct(previewProduct)}
        isSaved={previewProduct ? (existingSavedUrls.has(previewProduct.url) || savedProductUrls.has(previewProduct.url)) : false}
        onTryOn={() => {
          if (previewProduct) {
            setPreviewProduct(null);
            navigate('/app/try-on', { state: { productImage: previewProduct.image, productName: previewProduct.name } });
          }
        }}
        onShare={() => { setPreviewProduct(null); setShowFriendPicker(true); }}
        onViewDetails={async () => {
          if (!previewProduct) return;
          try {
            const saved = await createItem.mutateAsync({
              name: previewProduct.name,
              url: previewProduct.url,
              image_url: previewProduct.image,
              price: previewProduct.price || undefined,
              currency: previewProduct.currency,
              store: previewProduct.store,
              source_type: 'url',
              save_source: 'for_you',
            });
            setSavedProductUrls(prev => new Set(prev).add(previewProduct.url));
            setPreviewProduct(null);
            navigate(`/app/saved/${saved.id}`);
          } catch {
            toast.error('Could not load details');
          }
        }}
        formatPrice={formatPrice}
      />

      {/* Suggestion Friend Picker */}
      <SuggestionFriendPicker
        open={showFriendPicker}
        onOpenChange={setShowFriendPicker}
        product={previewProduct ? {
          name: previewProduct.name,
          url: previewProduct.url,
          image_url: previewProduct.image,
          price: previewProduct.price,
          currency: previewProduct.currency,
          store: previewProduct.store,
        } : null}
      />
    </div>;
}